import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES, PROJECT_STAGES_BY_KEY, SUB_STATUSES_BY_KEY } from "@/lib/workflow";
import { api, formatApiError } from "@/api/client";
import { LogOut, ChevronRight, AlertTriangle, Clock } from "lucide-react";
import Logo from "@/components/Logo";

/**
 * Mobile home for shop-floor roles (production / installation).
 * Lists projects currently in the user's owning stage(s) with quick access to
 * the project detail. Data is fetched from GET /api/project/ which the backend
 * already scopes for these roles (they can see all projects but aren't listed
 * as "assigned" — we surface the ones at their stage).
 */
const STAGES_FOR_ROLE = {
  production: ["production", "pre_installation"],
  installation: ["installation"],
};

export default function MobileMyProjects() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const roleMeta = ROLES[user?.role] || {};
  const myStages = STAGES_FOR_ROLE[user?.role] || [];

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/project/");
        // Filter to only stages this role owns
        const mine = (data || []).filter((p) => myStages.includes(p.stage));
        setProjects(mine);
      } catch (e) { setError(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="mobile-my-projects">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo className="h-10 w-10 shrink-0" />
          <div>
            <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Hygiene</div>
            <div className="font-black text-sm text-slate-900" style={{ fontFamily: "Chivo" }}>KITCHENS</div>
          </div>
        </div>
        <button type="button" data-testid="mobile-logout"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="text-slate-600 hover:text-slate-900 p-2" aria-label="Logout">
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      <main className="flex-1 p-4">
        <div className="max-w-sm mx-auto">
          <div className="mb-4">
            <div className="hk-tiny-label text-slate-500">Signed in as</div>
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>{user?.name}</h1>
            <div className="text-xs text-slate-500">{roleMeta.label}</div>
          </div>

          <div className="mb-3">
            <div className="hk-tiny-label" style={{ color: "#E52B50" }}>My work</div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
              {projects.length} project{projects.length === 1 ? "" : "s"} on my floor
            </h2>
          </div>

          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3 mb-3">{error}</div>}
          {loading ? (
            <div className="text-sm text-slate-500 text-center py-8">Loading…</div>
          ) : projects.length === 0 ? (
            <div className="hk-card p-6 text-center">
              <div className="hk-tiny-label" style={{ color: "#E52B50" }}>All clear</div>
              <h3 className="text-base font-bold text-slate-900 mt-1">No projects assigned right now</h3>
              <p className="text-xs text-slate-600 mt-2">You&apos;ll get a push notification when a new project reaches your stage.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {projects.map((p) => {
                const stageMeta = PROJECT_STAGES_BY_KEY[p.stage] || {};
                const blocked = p.sub_status && ["info_required", "on_hold"].includes(p.sub_status);
                const subMeta = p.sub_status ? SUB_STATUSES_BY_KEY[p.sub_status] : null;
                return (
                  <li key={p.id}>
                    <Link to={`/m/project/${p.id}`} className="block hk-card p-4 hover:shadow-md transition-shadow" data-testid={`mobile-project-${p.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 truncate">{p.customer_name} - {p.name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3" />
                            {p.estimated_delivery_date || "No delivery date"}
                          </div>
                          {p.special_requirements && (
                            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-1.5 mt-2" data-testid="mobile-special-req">
                              <b>Special:</b> {p.special_requirements}
                            </div>
                          )}
                          <div className="flex items-center gap-1 mt-2 flex-wrap">
                            <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-sm"
                              style={{ color: stageMeta.color, backgroundColor: stageMeta.bg }}>
                              {stageMeta.label || p.stage}
                            </span>
                            {blocked && subMeta && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-black uppercase px-1.5 py-0.5 rounded-sm"
                                style={{ color: subMeta.color, backgroundColor: subMeta.bg }}>
                                <AlertTriangle className="w-2.5 h-2.5" /> {subMeta.label}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
